import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, BookOpen, GraduationCap, FileText, Microscope, HeartHandshake, CheckCircle2, Star } from "lucide-react";
import SubjectsMarquee from "@/components/SubjectsMarquee";
import NewsletterSignup from "@/components/NewsletterSignup";
import { TESTIMONIALS } from "@/data/testimonials";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_vet-stem-tutoring/artifacts/zprhuk5u_Graduation.jpg";

const SERVICES = [
  {
    icon: Microscope,
    title: "STEM Tutoring",
    desc: "Biology, Chemistry, Physics, Biochem, and Anatomy. Taught by a practicing veterinarian who actually uses the material.",
    accent: "bg-forest text-paper-50",
  },
  {
    icon: HeartHandshake,
    title: "MMI Coaching",
    desc: "Station-by-station practice with live feedback. Learn the 3-beat structure that wins collaborative stations.",
    accent: "bg-terracotta text-paper-50",
  },
  {
    icon: GraduationCap,
    title: "Admissions Coaching",
    desc: "Timeline planning, school lists, and interview prep. Honest strategy for first-gen and non-traditional applicants.",
    accent: "bg-scholar text-ink",
  },
  {
    icon: FileText,
    title: "Essay Preparation",
    desc: "From the blank page to a personal statement that sounds like you, not a template or an AI.",
    accent: "bg-paper-100 text-ink border border-paper-200",
  },
];

const STEPS = [
  { n: "01", title: "Free intro call", desc: "A free 20-minute call. You share where you are. We map out what comes next." },
  { n: "02", title: "Your study map", desc: "We build a plan that fits your goals, your exams, and your life." },
  { n: "03", title: "Weekly 1:1 sessions", desc: "Real, focused, one-on-one study time. Retrieval practice, mock interviews, essay edits. Customizable based on your goals." },
  { n: "04", title: "Independence, by design", desc: "My goal is your independence. When you're confident with the subject matter, that's the real win." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden paper-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-paper-100 border border-paper-200 rounded-sm px-3 py-1.5">
              <Sparkles className="w-3.5 h-3.5 text-terracotta" />
              <span className="overline text-ink">Veterinarian · Mentor · First-Gen</span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-[5rem] tracking-tight leading-[0.95] text-ink mt-6">
              Navigating STEM with{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Veterinary Insight</span>
                <span className="absolute inset-x-0 bottom-1 h-3 lg:h-4 bg-scholar -z-0" aria-hidden />
              </span>
              .
            </h1>
            <p className="mt-7 text-lg text-ink-muted max-w-xl leading-relaxed">
              Hi, I'm <strong className="text-ink">Dr. Ashley Delmar, DVM, MRCVS</strong>. I'm a veterinarian passionate about
              empowering students to master complex STEM subjects through professional coaching, specialized essay preparation,
              and veterinary-inspired academic guidance. As a former first-generation and non-traditional student, I believe
              in helping students break through barriers to education and achieve success in their chosen fields.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-forest text-paper-50 px-7 py-4 text-base font-semibold rounded-sm hover:bg-forest-700 hover:-translate-y-0.5 transition-all"
              >
                Book your session <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 bg-paper-100 border border-paper-200 text-ink px-7 py-4 text-base font-semibold rounded-sm hover:bg-paper-200 transition-colors"
              >
                Explore services
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink-muted">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-forest" /> 1:1 personalized</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-forest" /> All STEM subjects</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-forest" /> MMI & essay expertise</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-sm overflow-hidden border border-paper-200 shadow-[0_1px_0_#E2DFD8,0_20px_40px_-20px_rgba(139,92,246,0.35)]">
              <img
                src={HERO_IMG}
                alt="Dr. Ashley Delmar at her DVM graduation"
                className="w-full h-[440px] lg:h-[560px] object-cover object-[50%_55%]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-ink/30 via-transparent to-transparent" />
            </div>
            <div className="hidden sm:block absolute -bottom-6 -left-6 bg-scholar text-ink p-5 w-[240px] rounded-sm shadow-xl">
              <p className="font-display font-black text-3xl leading-none">DVM</p>
              <p className="mt-2 text-sm font-medium">Clinical precision meets expert academic coaching.</p>
            </div>
            <div className="hidden sm:block absolute -top-5 -right-5 bg-terracotta text-paper-50 px-4 py-2.5 text-sm font-bold -rotate-3 rounded-sm shadow-lg">
              Vet-tested.<br />Student-approved.
            </div>
          </div>
        </div>
      </section>

      <SubjectsMarquee />

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline text-terracotta">What I help with</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-ink mt-3 max-w-2xl">
              Four services, built for students
              <br className="hidden sm:block" /> who don't fit a mold.
            </h2>
          </div>
          <Link to="/services" className="text-forest font-semibold link-underline" data-testid="services-link-all">
            See all details →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <div
              key={s.title}
              data-testid={`home-service-${i}`}
              className={`p-7 rounded-sm ${s.accent} group hover:-translate-y-1 transition-transform`}
            >
              <s.icon className="w-8 h-8 mb-6" strokeWidth={1.8} />
              <h3 className="font-display font-bold text-xl tracking-tight leading-snug">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed opacity-90">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS - bento */}
      <section className="bg-paper-100 border-y border-paper-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline text-terracotta">How I work</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-ink mt-3">
              Background, grades, gaps. None of it disqualifies you.
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-md">
              First-gen, returning student, late bloomer, career change. I've worked with all of them.
              The path forward looks different for every student, so we build one tailor-made for you.
            </p>
            <Link
              to="/contact"
              data-testid="process-cta"
              className="mt-7 inline-flex items-center gap-2 bg-terracotta text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-terracotta-dark transition-colors"
            >
              Start with a free call <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {STEPS.map((step) => (
              <div key={step.n} className="bg-paper-50 border border-paper-200 p-6 rounded-sm">
                <p className="font-display font-black text-4xl text-forest leading-none">{step.n}</p>
                <h3 className="font-display font-bold text-lg mt-4 text-ink">{step.title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <p className="overline text-terracotta">Student voices</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-ink mt-3">
              Real opinions from real students.
            </h2>
          </div>
        </div>
        <div
          className="relative mt-12 group"
          data-testid="testimonials-marquee"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)",
          }}
        >
          <div className="flex w-max gap-5 px-5 sm:px-8 animate-[marquee_120s_linear_infinite] group-hover:[animation-play-state:paused]">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i % TESTIMONIALS.length}`}
                className="w-[340px] sm:w-[380px] shrink-0 bg-paper-100 border border-paper-200 p-7 rounded-sm flex flex-col"
              >
                <div className="flex gap-0.5 text-scholar-dark mb-4">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <blockquote className="text-ink leading-relaxed text-[15px] flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-paper-200">
                  <p className="font-display font-bold text-ink">{t.name}</p>
                  <p className="text-sm text-ink-muted">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 flex justify-center sm:justify-start">
          <Link
            to="/testimonials"
            data-testid="home-read-more-reviews"
            className="group inline-flex items-center gap-2 text-ink font-display font-bold text-lg tracking-tight border-b-2 border-terracotta pb-1 hover:text-terracotta transition-colors"
          >
            Read more reviews
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-forest text-paper-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24 relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <BookOpen className="w-10 h-10 text-scholar mb-5" strokeWidth={1.6} />
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-2xl">
              Your future self is waiting. Let's get you there.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="home-cta-band"
            className="inline-flex items-center gap-2 bg-scholar text-ink px-7 py-4 text-base font-semibold rounded-sm hover:bg-scholar-dark hover:-translate-y-0.5 transition-all self-start"
          >
            Book your free intro call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-forest-700 opacity-50" aria-hidden />
        <div className="absolute -left-10 -bottom-20 w-72 h-72 rounded-full bg-terracotta opacity-20" aria-hidden />
      </section>

      <NewsletterSignup variant="band" testid="home-newsletter" />
    </div>
  );
}
